import './LoginWeb3.css';
import { Button, Modal, Switch } from '../../ui';
import { useDevice, useLoginWeb3 } from '../../../hooks';
import { Link } from 'react-router-dom';
import { ValidateEmail } from '../web2/ValidateEmail';
import { SignupStatuses } from '../../../enums';
import { useMemo } from 'react';

// Helper function to format wallet address
const formatWalletAddress = (address: string, startLength: number = 10, endLength: number = 10): string => {
   return `${address.substring(0, startLength)}...${address.substring(address.length - endLength)}`;
};

export function LoginWeb3(props: { title?: string; header?: boolean; showConnectedWallet?: boolean }) {
   const {
      ledgerExpanded,
      setLedgerExpanded,
      isLedger,
      setIsLedger,
      authenticateUser,
      disconnectWallet,
      isAuthenticated,
      wallet,
      // setAuthRequested,
      showWalletConfirmModal,
      loading,
      handleConnect,
      verificationStatus,
      submitVerificationCode,
      resendVerificationCode,
      cancelVerification,
      user,
   } = useLoginWeb3();
   const { isMobile } = useDevice();

   const walletAddress = wallet?.publicKey?.toString();
   const isVerificationMode = verificationStatus === SignupStatuses.VerificationEmailSent && user?.email;

   const walletButton = useMemo(() => {
      if (props.showConnectedWallet && walletAddress) {
         return {
            title: `${formatWalletAddress(walletAddress, 5, 5)} - Disconnect`,
            onClick: disconnectWallet,
         };
      }
      return {
         title: props.title || 'Select Wallet',
         onClick: handleConnect,
      };
   }, [props.showConnectedWallet, walletAddress, handleConnect, disconnectWallet, props.title]);

   const handleModalClose = () => {
      disconnectWallet();
      cancelVerification();
   };

   return (
      <div className={`flex flex-col justify-between h-100 ${props.header ? 'items-center' : ''} gap-3 `}>
         {props.header && (
            <div className="flex items-center gap-2">
               {props.showConnectedWallet && walletAddress ? (
                  <div className="wallet-img-container">
                     <p>{formatWalletAddress(walletAddress)}</p>
                  </div>
               ) : (
                  <h3>Connect wallet</h3>
               )}
            </div>
         )}

         {props.header && !props.showConnectedWallet && (
            <div className="wallet-img-container">
               <img
                  src="/assets/icons/wallet-connect-icon.svg"
                  alt="wallet"
               />
            </div>
         )}

         {!showWalletConfirmModal ? (
            <Button
               title={walletButton.title}
               type={props.showConnectedWallet && walletAddress ? 'transparent-big' : 'white-big'}
               onClick={walletButton.onClick}
            />
         ) : (
            <Modal
               className="auth-provider"
               onClose={handleModalClose}
               active={isAuthenticated !== undefined && walletAddress !== undefined}
               darkBackground>
               <div className="flex items-center flex-col mb-1 gap-3">
                  <img
                     src="/assets/images/golfn-logo.png"
                     alt="logo"
                     width={isMobile ? 80 : 100}
                  />
                  <h4>Confirm Wallet</h4>
               </div>

               <div className="flex flex-col gap-2">
                  {isVerificationMode && user?.email ? (
                     <ValidateEmail
                        email={user.email}
                        onSubmit={submitVerificationCode}
                        onCancel={cancelVerification}
                        isLoading={loading}
                        onResend={resendVerificationCode}
                     />
                  ) : (
                     <>
                        <div className="flex flex-col gap-1">
                           <div className="wallet-adr-container">
                              <p>{walletAddress && formatWalletAddress(walletAddress)}</p>
                           </div>
                           <p>* Please note that no amount will be charged by confirming.</p>
                           <p>
                              * By confirming, you agree to the{' '}
                              <Link
                                 to={'/terms-and-conditions'}
                                 target="_blank">
                                 Terms of Service
                              </Link>
                           </p>
                        </div>

                        <div className={`ledger-accordion ${ledgerExpanded ? 'open' : ''}`}>
                           <div className="flex gap-small justify-end items-center ledger-accordion-header">
                              <img
                                 src={'/assets/icons/arrow-back.svg'}
                                 alt=""
                              />
                              <span onClick={() => setLedgerExpanded(!ledgerExpanded)}>Using a Ledger?</span>
                           </div>
                           <div className="ledger-accordion-content flex gap items-center">
                              <Switch
                                 checked={isLedger}
                                 onChange={setIsLedger}
                              />
                              <p>I'm using a Ledger</p>
                           </div>
                        </div>

                        <div className="flex flex-col gap-2">
                           <Button
                              title="Confirm wallet"
                              onClick={authenticateUser}
                              type="white-big"
                              disabled={loading}
                              loading={loading}
                           />
                           <div className="flex justify-center">
                              <Button
                                 title="Cancel"
                                 onClick={disconnectWallet}
                                 type="transparent"
                                 disabled={loading}
                              />
                           </div>
                        </div>
                     </>
                  )}
               </div>
            </Modal>
         )}
      </div>
   );
}
